import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import type { CustomerRow } from '@/Pages/CRM/lib';
import { apiError, apiStatus } from './lib';

/**
 * ADR-0021 §2.6 — a phone lookup is a PII search, not the generic customer
 * search: its own permission (`customers.search_phone`, checked one level up
 * by `CallCenter.tsx` before this even mounts), its own throttled route
 * (`GET /call-center/customers/search`, 20/min server-side — this screen
 * does not add a second client-side limiter on top of a server one it
 * cannot see the state of, it just never offers a way to fire the request
 * faster than a human typing a number), and a masked, count-only audit row
 * written server-side. There is deliberately no "search everyone" button and
 * no result list wider than what the typed digits actually match — a bulk
 * lookup affordance is exactly what this permission split exists to prevent.
 */
export default function PhoneLookup({
  selected,
  onSelect,
}: {
  selected: CustomerRow | null;
  onSelect: (customer: CustomerRow | null) => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canCreate = can('customers.create');

  const [phone, setPhone] = useState('');
  const [results, setResults] = useState<CustomerRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const search = () => {
    const query = phone.trim();
    if (query.length < 3) {
      setError(t('callCenter.lookup.tooShort'));
      setResults(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<{ data: CustomerRow[] }>('/call-center/customers/search', {
        params: { phone: query, limit: 10 },
      })
      .then(({ data }) => setResults(data.data ?? []))
      .catch((e) => {
        if (apiStatus(e) === 429) {
          setError(t('callCenter.lookup.throttled'));
        } else {
          setError(apiError(e, t('callCenter.lookup.searchFailed')));
        }
        setResults(null);
      })
      .finally(() => setLoading(false));
  };

  const createCaller = async () => {
    const query = phone.trim();
    setCreating(true);
    try {
      const { data } = await api.post<CustomerRow>('/customers', { phone: query });
      toast.success(t('callCenter.lookup.callerCreated'));
      onSelect(data);
      setResults(null);
    } catch (e) {
      toast.error(apiError(e, t('callCenter.lookup.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('callCenter.lookup.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-2 rounded-lg border border-line-strong bg-surface p-2.5 text-xs text-ink-muted">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          <span>{t('callCenter.lookup.piiNote')}</span>
        </div>

        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            search();
          }}
        >
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('callCenter.lookup.placeholder')}
            aria-label={t('callCenter.lookup.title')}
          />
          <Button type="submit" disabled={loading || !phone.trim()}>
            {t('common.search')}
          </Button>
        </form>

        {selected && (
          <div className="flex items-center justify-between rounded-lg border border-accent bg-accent-soft p-3 text-sm">
            <div>
              <div className="font-medium">{selected.name ?? t('crm.customers.unnamed')}</div>
              <div className="text-xs text-ink-muted">{selected.phone}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onSelect(null)}>
              {t('callCenter.lookup.clear')}
            </Button>
          </div>
        )}

        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={error} onRetry={search} />
        ) : results !== null ? (
          results.length === 0 ? (
            <EmptyState
              title={t('callCenter.lookup.noMatch')}
              hint={t('callCenter.lookup.noMatchHint')}
              action={
                canCreate && (
                  <Button size="sm" onClick={() => void createCaller()} disabled={creating}>
                    {t('callCenter.lookup.createCaller', { phone: phone.trim() })}
                  </Button>
                )
              }
            />
          ) : (
            <ul className="scrollbar-thin max-h-72 divide-y overflow-y-auto rounded-lg border">
              {results.map((row) => (
                <li key={row.id}>
                  <button
                    onClick={() => {
                      onSelect(row);
                      setResults(null);
                    }}
                    className="w-full p-3 text-start text-sm hover:bg-hover"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{row.name ?? t('crm.customers.unnamed')}</span>
                      <Badge tone="neutral">{row.phone}</Badge>
                    </div>
                    {row.email && <div className="text-xs text-ink-muted">{row.email}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}
